import { DVV_CALL_TYPE, DVV_EVENT_TYPE, WEB_RTC_Constants } from "./Constants";
import VideoCallMgr from "../../util/callutil/VideoCallMgr";
import { CALL_MESSAGE_TYPE, CALL_STATE } from "../../model/Constants";
import MediaDeviceMgr from "../../util/callutil/VideoDeviceMgr";
import { createOption, createVideoElement } from "../../util/HTMLElements";
import pubSubGlobal from "../../util/pubSubGlobal";
import { debounce } from "../../util/Misc";
import shoutUIComponent from "../../../model/shoutUIComponent";
import {videoStreamRender} from "../templates/renders/videoStream";

interface TypeDef_VideoStream extends shoutUIComponent {
    constants?: {};
    $app?: any;
    renderer: any;
    toggleMicrophoneOnOff: () => void;
    toggleVideoOnOff: () => void;
    endCall: () => void;
    switchToCallView: () => void;
    startCall: (config: any) => void;
    setCallState: (state: any) => void;
    showVideoConnectingView: () => void;
    removeLocalMediaStram: (selector: any) => void;
    handleRoomStatus: (status: any, stream: any) => void;
    showCallTransferState: () => void;
    hideCallTransferState: () => void;
    showDeviceSettings: () => void;
    unmuteVolume: () => void;
    muteVolume: () => void;
    setVolumeHandler: () => void;
    hideDeviceSettings: () => void;
    setSinkID: (sinkID: any) => void;
    setAudioVideoSource: (audioSrc: any, videoSrc: any) => void;
    setVolume: (e: any) => void;
    clear: () => void;
    injectRemoteVideoElement: (stream: any) => void;
    setDefaultVolume: () => void;
    injectCustomerVideoElement: (stream: any) => void;
    updateCounter: () => void;
    showChatView: () => void;
    expandVideoContainer: () => void;
}

/**
 * Custom element which act as a container for video call
 * @type {{template, created(), tagName: string, onCancelClick(): void, onOkClick(): void}}
 */
// eslint-disable-next-line object-shorthand
const VideoStream: TypeDef_VideoStream = {
    tagName: "video-stream-view",
    renderer: videoStreamRender,
    created() {
        this.videoContainer = document.querySelector(".video-container");
        this.settingsEl = this.el.querySelector(".media-settings-container");
        this.mediaDeviceManager = new MediaDeviceMgr(this);
        this.callState = CALL_STATE.CALL_INVITED;
        this.agentMsg = this.$app.getPersistedValue(WEB_RTC_Constants.UNREAD_COUNT, 0);
        this.muteVideo = false;
        this.muteAudio = false;
        this.videoOnBtn = this.el.querySelector(".video-on-icon");
        this.expandVideoBtn = this.el.querySelector(".video-expand-icon");
        this.videoOffBtn = this.el.querySelector(".video-off-icon");
        this.micOnBtn = this.el.querySelector(".mic-on-icon");
        this.micOffBtn = this.el.querySelector(".mic-off-icon");
        this.counterEl = this.el.querySelector(".chat-notification-count");
        this.videoCallView = this.el.querySelector(".video-call");
        this.errorView = this.el.querySelector(".call-error");
        this.unsupportedDeviceErrorView = this.el.querySelector(".unsupported-device-error");
        this.connectingView = this.el.querySelector(".call-connecting");
        this.chatNotificationContainer = this.el.querySelector(".chat-notification-block");
        this.videoChatControls = this.el.querySelector(".video-chat-controls");
        this.videoExpandContainer = this.el.querySelector(".video-expand-icon-block");
        this.agentAliasOuterDiv = this.el.querySelector(".agent-alias-outer-div");
        this.agentAliasTextEl = this.el.querySelector(".agent-alias-text");
        this.callTransferTextEl = this.el.querySelector(".call-transfer-text");
        this.noCustomerVideoEl = this.el.querySelector(".video-no-customer-video");
        const agentBubbleEl: any = document.querySelector(".agent_Agent span");
        this.data.agentAliasText = agentBubbleEl && agentBubbleEl.innerText
            ? agentBubbleEl.innerText : WEB_RTC_Constants.AGENT_ALIAS_TEXT;
        this.showVideoConnectingView();
    },
    toggleMicrophoneOnOff(): void {
        /**
         * An array of MediaStreamTrack objects, one for each audio track contained in the stream.
         * We are looking at 0 because we are attaching only one stream to video element
         */
        this.localStream.getAudioTracks()[0].enabled = this.muteAudio;
        this.muteAudio = !this.muteAudio;
        this.micOnBtn.classList.toggle("hide");
        this.micOffBtn.classList.toggle("hide");
    },
    toggleVideoOnOff(): void {
        /**
         * An array of MediaStreamTrack objects, one for each video track contained in the stream.
         * We are looking at 0 because we are attaching only one stream to video element
         */
        this.localStream.getVideoTracks()[0].enabled = this.muteVideo;
        this.muteVideo = !this.muteVideo;
        this.localVideoEl.classList.toggle("hide");
        this.videoOnBtn.classList.toggle("hide");
        this.videoOffBtn.classList.toggle("hide");
    },

    /**
     * End and close webrtc call window
     */
    endCall(): void {
        this.clear();
        this.$app.sendWebRTCInvitationState(CALL_MESSAGE_TYPE.CALL_ENDED);
        this.setCallState(CALL_STATE.CALL_ENDED);
        this.$app.removePersistedValueByKey(WEB_RTC_Constants.UNREAD_COUNT);
        this.$app.removePersistedValueByKey(WEB_RTC_Constants.CALL_STATE);
        this.$app.shoutui.getLayoutEl().removeVideoCallContainer();
    },

    /**
     * Switch call window from unsupported device view
     */
    switchToCallView(): void {
        this.videoCallView.classList.add("show");
        this.connectingView.classList.add("hide");
        this.unsupportedDeviceErrorView.classList.remove("show");
        this.chatNotificationContainer.classList.remove("hide");
    },

    /**
     * Allow customer to join the room created by agent
     * @param {Object} config video/audio call configuraion
     */
    startCall(config: any): void {
        this.devices = {};

        if (this.prop.audiosrc) {
            this.devices.audio = { deviceId: { exact: this.prop.audiosrc } };
        }

        if (this.prop.videosrc) {
            this.devices.video = { deviceId: { exact: this.prop.videosrc } };
        }

        this.callMgr = new VideoCallMgr(this.$app,
            this.simulcast,
            this.simulcast2,
            this.devices,
            this.data.showCustomerCamera);

        this.callMgr.startVideoCallEngagement(config, (status: any, stream: any) => {
            this.handleRoomStatus(status, stream);
        });

        this.onNewAgentMsgSubscriptionId = pubSubGlobal.subscribe(() => {
            this.agentMsg = parseInt(this.agentMsg, 10) + 1;
            this.updateCounter();

            // urc mean unread count, trying to save cookie space
            this.$app.persistValue(WEB_RTC_Constants.UNREAD_COUNT, this.agentMsg);
        }, "new.agentMsg");
    },
    setCallState(state: any): void {
        this.callState = state;
        this.$app.persistValue(WEB_RTC_Constants.CALL_STATE, state);
    },
    showVideoConnectingView(): void {
        if (this.data.showCustomerCamera) {
            const localVideoEl = createVideoElement("connecting-video-screen", true);
            this.connectingView.appendChild(localVideoEl);

            const constraints = {
                audio: false,
                video: this.prop.videosrc ? { deviceId: { exact: this.prop.videosrc } } : true,
            };
            navigator.mediaDevices.getUserMedia(constraints)
                .then(function (stream) {
                    if (localVideoEl) {
                        localVideoEl.srcObject = stream;
                    }
                })
                .catch(function (err) {
                    console.log("Error in video connection screen", err);
                });
        }
    },

    /**
     * Detach media stream from element
     * @param {selector} selector element selector criteria
     */
    removeLocalMediaStram(selector: any): void {
        const videoEl = document.querySelector(selector);

        if (videoEl) {
            const stream = videoEl.srcObject;
            const tracks = stream.getTracks();
            tracks.forEach(function (track: any) {
                track.stop();
            });
            videoEl.srcObject = null;
            videoEl.remove();
        }
    },

    /**
     * room status published by webrtc gateway, using the room status ci controls the various UI element visibility.
     * @param {int} status current status of call
     * @param {MediaStream} stream media stream object
     */
    handleRoomStatus(status: any, stream: any): void {
        let dvvType = DVV_CALL_TYPE.VIDEO_CALL_ONE_WAY;

        if (this.data.showCustomerCamera) {
            dvvType = DVV_CALL_TYPE.VIDEO_CALL_TWO_WAY;
        } else if (this.data.voiceOnlyCall) {
            dvvType = DVV_CALL_TYPE.VOICE_ONLY;
        }

        switch (status) {
            case WEB_RTC_Constants.ROOM_JOINED:
                this.setCallState(CALL_STATE.ROOM_JOINED);
                break;

            case WEB_RTC_Constants.RTC_ERROR:
                this.callState = CALL_STATE.CALL_ERROR;
                this.videoCallView.classList.remove("show");
                this.chatNotificationContainer.classList.add("hide");
                this.errorView.classList.add("show");
                this.connectingView.classList.add("hide");
                this.$app.logWebRTCEvents(DVV_EVENT_TYPE.CUSTOMER_FAILED_CALL, dvvType);
                break;

            case WEB_RTC_Constants.LOCAL_AUDIO_ERROR:
                this.callState = CALL_STATE.CALL_ERROR;
                this.videoCallView.classList.remove("show");
                this.chatNotificationContainer.classList.add("hide");
                this.unsupportedDeviceErrorView.classList.add("show");
                this.connectingView.classList.add("hide");
                break;

            case WEB_RTC_Constants.AGENT_VIDEO_ERROR:
                this.noRemoteVideoEl.classList.remove("hide");
                this.remoteVideoEl.classList.add("hide");
                break;

            case WEB_RTC_Constants.AGENT_VIDEO_PUBISHED:
                this.callState = CALL_STATE.AGENT_PUBLISHED;
                this.injectRemoteVideoElement(stream);
                this.updateCounter();
                this.videoCallView.classList.add("show");
                this.connectingView.classList.add("hide");
                this.chatNotificationContainer.classList.remove("hide");

                if (!this.data.voiceOnlyCall) {
                    this.noRemoteVideoEl.classList.add("hide");
                }

                this.setSinkID(this.prop.sinkid);

                if (this.data.showCustomerCamera) {
                    this.removeLocalMediaStram(".connecting-video-screen");
                }

                break;

            case WEB_RTC_Constants.LOCAL_VIDEO_ERROR:
                this.noLocalVideoEl.classList.remove("hide");
                this.localVideoEl.classList.add("hide");
                break;

            case WEB_RTC_Constants.CUSTOMER_VIDEO_PUBISHED:
                this.callState = CALL_STATE.CUSTOMER_PUBLISHED;
                this.injectCustomerVideoElement(stream);
                break;

                // no default
        }
    },

    /**
     * This methods hide existing call view and shows connecting call screen
     * on call transfer event received from agent
     */
    showCallTransferState(): void {
        if (this.data.voiceOnlyCall) {
            if (this.noRemoteVideoEl) {
                this.noRemoteVideoEl.classList.add("hide");
            }

            if (this.noCustomerVideoEl) {
                this.noCustomerVideoEl.classList.add("call-transfer-no-customer-video");
            }
        } else {
            if (this.remoteVideoEl) {
                this.remoteVideoEl.classList.add("hide");
            }

            if (this.localVideoEl) {
                this.localVideoEl.classList.add("remote-video-preview");
                this.localVideoEl.classList.remove("local-video-preview");
            }
        }

        if (this.callTransferTextEl) {
            this.callTransferTextEl.classList.remove("hide");
            this.callTransferTextEl.classList.add("show");
        }
    },

    /**
     * This methods hide  call transfer  screen and shows call screen
     * on successull call transfer event received from agent
     */
    hideCallTransferState(): void {
        if (this.data.voiceOnlyCall) {
            if (this.noRemoteVideoEl) {
                this.noRemoteVideoEl.classList.remove("hide");
            }

            if (this.noCustomerVideoEl) {
                this.noCustomerVideoEl.classList.remove("call-transfer-no-customer-video");
            }
        } else {
            if (this.remoteVideoEl) {
                this.remoteVideoEl.classList.remove("hide");
            }

            if (this.localVideoEl) {
                this.localVideoEl.classList.remove("remote-video-preview");
                this.localVideoEl.classList.add("local-video-preview");
            }
        }

        if (this.callTransferTextEl) {
            this.callTransferTextEl.classList.add("hide");
            this.callTransferTextEl.classList.remove("show");
        }
    },
    showDeviceSettings(): void {
        this.settingsEl.style.display = "block";
        this.audioInputSelect = this.el.querySelector("#inputDevices");
        this.audioOutputSelect = this.el.querySelector("#outputDevices");
        this.videoSelect = this.el.querySelector("#videoDevices");
        this.volControl = this.el.querySelector("#vol-control");
        this.setVolumeHandler();

        if (this.audioInputSelect.childNodes.length == 0 && this.devices.audio) {
            const src = this.devices.audio.deviceId.exact;
            this.audioInputSelect.add(createOption(src, src));
        }

        if (this.videoSelect && this.videoSelect.childNodes.length == 0 && this.devices.video) {
            const src = this.devices.video.deviceId.exact;
            this.videoSelect.add(createOption(src, src));
        }

        if (this.audioOutputSelect.childNodes.length == 0 && this.prop.sinkid) {
            this.audioOutputSelect.add(createOption(this.prop.sinkid, this.prop.sinkid));
        }

        this.volControl.value = parseFloat(this.prop.vollevel) * 100;
        this.mediaDeviceManager.showMediaDeviceSettings(
            null,
            this.audioInputSelect,
            this.audioOutputSelect,
            this.videoSelect
        );
    },
    unmuteVolume(): void {
        this.volControl.value = 100;
        this.remoteVideoEl.volume = 1;
    },
    muteVolume(): void {
        this.volControl.value = 0;
        this.remoteVideoEl.volume = 0.0;
    },
    setVolumeHandler(): void {
        this.debouncedVolHandler = debounce((e: any) => {
            if (this.remoteVideoEl) {
                const val = parseFloat(e.target.value);
                this.remoteVideoEl.volume = val / 100;
            }
        }, 100);
    },
    hideDeviceSettings(): void {
        this.settingsEl.style.display = "none";

        if (this.mediaDeviceManager) {
            this.mediaDeviceManager.clear();
        }
    },
    setSinkID(sinkID: any): void {
        if (sinkID) {
            this.mediaDeviceManager.attachSinkId(this.remoteVideoEl, sinkID);
        }
    },
    setAudioVideoSource(audioSrc: any, videoSrc: any): void {
        let newDevices = false;

        if (audioSrc && (audioSrc !== "default" || this.devices.audio)
            && (!this.devices.audio || audioSrc !== this.devices.audio.deviceId.exact)
        ) {
            this.devices.audio = { deviceId: { exact: audioSrc } };
            newDevices = true;
        }

        if (videoSrc && (videoSrc !== "default" || this.devices.video)
            && (!this.devices.video || videoSrc !== this.devices.video.deviceId.exact)
        ) {
            this.devices.video = { deviceId: { exact: videoSrc } };
            newDevices = true;
        }

        if (newDevices) {
            this.callMgr.replaceOwnFeed(this.devices, true, (status: any) => {
                this.handleRoomStatus(status);
            });
        }
    },
    setVolume(e: any): void {
        this.debouncedVolHandler(e);
    },
    clear(): void {
        if (this.callMgr) {
            this.callMgr.closeAllMediaDevices(this.localStream);
            this.localStream = null;
            this.callMgr = null;
        }

        if (this.mediaDeviceManager) {
            this.mediaDeviceManager.clear();
        }

        if (this.data.showCustomerCamera) {
            this.removeLocalMediaStram(".connecting-video-screen");
        }

        pubSubGlobal.unsubscribe(this.onNewAgentMsgSubscriptionId);
    },
    removed(): void {
        this.clear();
    },
    injectRemoteVideoElement(stream: any): void {
        const remoteContainer = this.el.querySelector(".remote-video-container");

        if (!this.remoteVideoEl) {
            this.remoteVideoEl = createVideoElement("remote-video-preview", false);
            remoteContainer.appendChild(this.remoteVideoEl);
            this.setDefaultVolume();
            this.noRemoteVideoEl = remoteContainer.firstElementChild;
        }

        this.remoteVideoEl.classList.remove("hide");
        this.callMgr.attachStream(this.remoteVideoEl, stream);
    },
    setDefaultVolume(): void {
        if (this.prop.vollevel) {
            this.remoteVideoEl.volume = parseFloat(this.prop.vollevel);
        }
    },
    injectCustomerVideoElement(stream: any): void {
        if (this.data.showCustomerCamera) {
            const localContainer = this.el.querySelector(".local-video-container");

            if (!this.localVideoEl) {
                this.localVideoEl = createVideoElement("local-video-preview", true);
                localContainer.appendChild(this.localVideoEl);
                this.noLocalVideoEl = localContainer.firstElementChild;
            }

            this.noLocalVideoEl.classList.add("hide");
            this.localVideoEl.classList.remove("hide");

            this.callMgr.attachStream(this.localVideoEl, stream);
            this.muteVideo = false;
            this.videoOnBtn.classList.remove("hide");
            this.videoOffBtn.classList.add("hide");
        }

        this.localStream = stream;
        this.localStream.getAudioTracks()[0].enabled = !this.muteAudio;
    },
    updateCounter(): void {
        if (this.agentMsg === undefined) {
            this.agentMsg = 0;
        }

        this.counterEl.innerText = this.agentMsg;
    },

    /**
     * This method shrink video container view and show chat view so that
     * user can chat simultaneously while video call in progress
     */
    showChatView(): void {
        this.chatNotificationContainer.classList.add("hide");
        this.videoChatControls.classList.add("hide");
        this.videoContainer.classList.add("shrink-video-container");
        this.videoExpandContainer.classList.remove("hide");
        this.agentMsg = 0;
        this.updateCounter();

        if (this.data.voiceOnlyCall) {
            this.agentAliasOuterDiv.classList.add("agent-alias-outer-div-collapse");
            this.agentAliasTextEl.classList.add("font-size-large");
        }
    },

    /**
     * This method resizes video container view and  hide chat view
     */
    expandVideoContainer(): void {
        this.agentMsg = 0;
        this.updateCounter();
        this.chatNotificationContainer.classList.remove("hide");
        this.videoChatControls.classList.remove("hide");
        this.videoContainer.classList.remove("shrink-video-container");
        this.videoExpandContainer.classList.add("hide");

        if (this.data.voiceOnlyCall) {
            this.agentAliasOuterDiv.classList.remove("agent-alias-outer-div-collapse");
            this.agentAliasTextEl.classList.remove("font-size-large");
        }
    },
};

/**
 * @export
 * @param   {CIApp} appInstance App instance
 * @return  {VideoStream}    View instance
 */
export default (appInstance: any) => {
    const appConfig = appInstance.getUIConfig();
    const appStringProps = appConfig.appStringProps;
    const dVVConfig = appConfig.digitalVoiceAndVideoConfig;
    const imgPath = appInstance.getImagePath();
    VideoStream.$app = appInstance;

    /**
     * @type {Object}
     */
    const constObj: any = {};

    if (dVVConfig) {
        if (dVVConfig.images) {
            constObj.noAgentVideoImg = imgPath + dVVConfig.images.noAgentVideo;
            constObj.noCustomerVideoImg = imgPath + dVVConfig.images.noCustomerVideo;
            constObj.callErrorImg = imgPath + dVVConfig.images.callError;
            constObj.speakerMuteImg = imgPath + dVVConfig.images.speakerMuteImg;
            constObj.speakerVolumeImg = imgPath + dVVConfig.images.speakerVolumeImg;
        }

        /*
         *simulcast =true will enable old-style simulcasting
         *(SDP munging for Chrome and Safari, rid-based for Firefox)
         */
        constObj.simulcast = dVVConfig.enableSimulcast;

        /*
         *simulcast2 =true will enable the new rid-based simulcasting on Chrome M74 and M75
         */
        constObj.simulcast2 = dVVConfig.enableSimulcast2;
    }

    constObj.endCallButtonText = appStringProps.endCallButtonText || WEB_RTC_Constants.END_CALL_BUTTON_TEXT;
    constObj.callErrorText = appStringProps.callErrorText || WEB_RTC_Constants.CALL_ERROR_TEXT;
    constObj.unsupportedDeviceErrorText = appStringProps.unsupportedDeviceErrorText || WEB_RTC_Constants.UNSUPPORTED_DEVICE_TEXT;
    VideoStream.constants = constObj;

    VideoStream.data = {
        agentAliasText: WEB_RTC_Constants.AGENT_ALIAS_TEXT,
        isVoiceCall: (state: any) => state.prop.videoenabled !== "true",
        connectingText: (state:any) => (state.prop.reconnect == "true" ? appStringProps.callReconnectingText
            : appStringProps.callConnectingText),
        showCustomerCamera:
            (state: any) => state.prop[WEB_RTC_Constants.ENABLED_CUSTOMER_CAMERA.toLowerCase()] === "true",
        voiceOnlyCall:
            (state: any) => state.prop[WEB_RTC_Constants.ENABLED_VOICE_ONLY_CALL.toLowerCase()] === "true",
        callTransferText: WEB_RTC_Constants.CALL_TRANSFER_TEXT,
    };
    return VideoStream;
};
